// The products of a company (`orgId`) or of every company (none: Admin → Store
// → User Feedback), each with what buyers said: the stars, how many wrote, how
// many asked to send it back and why most often. Sortable by any column,
// searchable, paged (aldoo-ra/DataTable: page size, pages); a product opens its own feedback (ProductFeedback).
import React, { useCallback, useEffect, useMemo, useState } from "react"
import moment from "moment"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import { useUrlParam } from "aldoo-ra/URLState"
import DataTable from "aldoo-ra/DataTable"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { FeedbackProducts } from "../api"
import { Thumb } from "../ui"
import ProductFeedback, { REASON_LABELS, Stars } from "./ProductFeedback"
import usePortalTexts from "../../Vendor/texts/usePortalTexts"
import { FEEDBACK, SALES } from "../../Vendor/texts/bundles"
import useSiteLanguage from "../../Storefront/hooks/useSiteLanguage"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the help center's article on the feedback (aldoo-com-api scripts/help-center/vendor/articles/ratings-and-comments.html)
const HELP = { table: "vendor.feedback#table", product: "vendor.feedback#product" }

const PER_PAGE = 25

// the period, as on Sales (aldoo-ra/StoreSales): all of it unless narrowed — feedback is sparse
const DATE_PRESETS = [
  { id: "last24h", label: "Last 24 Hours", value: { days: 1 } },
  { id: "last7d", label: "Last 7 Days", value: { days: 7 } },
  { id: "last30d", label: "Last 30 Days", value: { days: 30 } },
  { id: "last90d", label: "Last 90 Days", value: { days: 90 } },
  { id: "last365d", label: "Last 365 Days", value: { days: 365 } },
  { id: "lifetime", label: "Lifetime", value: null },
  { id: "custom", label: "Custom Range", value: "custom" },
]
const DEFAULT_FILTER = { type: "preset", value: null }
const dayOf = (date) => moment(date).format("YYYY-MM-DD")
/** The range the API takes (calendar days, both counted), from what the dropdown answers with. */
const rangeOf = (filter) => {
  if (filter?.type === "custom" && filter.range?.startDate && filter.range?.endDate) return { startDate: dayOf(filter.range.startDate), endDate: dayOf(filter.range.endDate) }
  const days = filter?.value?.days
  if (!days) return { lifetime: true }
  return { startDate: dayOf(moment().subtract(days - 1, "days")), endDate: dayOf(new Date()) }
}

export default function FeedbackTable({ orgId = null, showVendor = false }) {
  const t = usePortalTexts(FEEDBACK)
  // the period's words, as Sales has them
  const ts = usePortalTexts(SALES)
  const datePresets = useMemo(() => DATE_PRESETS.map((preset) => ({ ...preset, label: ts(preset.label) })), [ts])
  // in the address, so a reload or a shared link keeps it
  const [filterParam, setFilterParam] = useUrlParam("feedback_period", { default: "" })
  const filter = useMemo(() => {
    try {
      return filterParam ? JSON.parse(filterParam, (key, v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v) ? new Date(v) : v)) : DEFAULT_FILTER
    } catch {
      return DEFAULT_FILTER
    }
  }, [filterParam])
  const setFilter = useCallback((next) => setFilterParam(!next || JSON.stringify(next) === JSON.stringify(DEFAULT_FILTER) ? "" : JSON.stringify(next)), [setFilterParam])
  const range = useMemo(() => rangeOf(filter), [filter])
  const rangeKey = JSON.stringify(range)
  const language = useSiteLanguage()
  const locale = language === "bg" ? "bg-BG" : "en-GB"
  const [search, setSearch] = useState("")
  const [term, setTerm] = useState("")
  const [totals, setTotals] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(null)
  // the open product's thumbnail and store url, once its feedback has loaded
  const [product, setProduct] = useState(null)

  // the search waits for the typing to settle
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // a page of the table (aldoo-ra/DataTable: its page, page size and sort); the totals come with it
  const apiMethod = async ({ page = 1, itemsPerPage = PER_PAGE, sort }) => {
    const res = await FeedbackProducts({
      ...(orgId ? { company: String(orgId) } : {}),
      search: term,
      sort: sort?.field || "engagement",
      direction: sort?.field ? sort.order : "desc",
      active: true,
      page,
      items_per_page: itemsPerPage,
      language,
      dateRange: range,
    }).catch((err) => ({ error: err.message }))
    if (res?.error) {
      setError(res.message || res.error)
      return { list: [], total: 0 }
    }
    setError(null)
    setTotals(res.totals)
    return res
  }

  const center = "text-center tabular-nums"
  const columns = [
    {
      id: "name",
      title: t`Product`,
      sortable: true,
      render: (_, row) => (
        <div className="min-w-0">
          <div className="font-medium">{row.name}</div>
          <div className="text-xs text-admin_text_secondary">{row.pid}</div>
        </div>
      ),
    },
    ...(showVendor ? [{ id: "vendor", title: t`Vendor`, render: (_, row) => row.vendor?.name || "—" }] : []),
    {
      id: "rating",
      title: t`Rating`,
      sortable: true,
      render: (_, row) =>
        row.ratings ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Stars value={row.average} />
            <span className="tabular-nums">
              {row.average.toFixed(1)}
            </span>
          </span>
        ) : (
          "—"
        ),
    },
    {
      id: "ratings",
      title: t`Ratings`,
      sortable: true,
      render: (_, row) => <div className={center}>{row.ratings}</div>,
    },
    {
      id: "comments",
      title: t`Written reviews`,
      sortable: true,
      render: (_, row) => <div className={center}>{row.comments}</div>,
    },
    {
      id: "returns",
      title: t`Return requests / returns`,
      sortable: true,
      render: (_, row) => (
        <div className={center}>
          {row.returnRequests} / {row.returns}
        </div>
      ),
    },
    {
      id: "units",
      title: t`Units returned`,
      sortable: true,
      render: (_, row) => <div className={center}>{row.returnedUnits}</div>,
    },
    {
      id: "topReason",
      title: t`Top reason`,
      render: (_, row) => (
        <div className="text-center">
          {row.topReason ? t(REASON_LABELS[row.topReason] || row.topReason) : "—"}
        </div>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-4" data-testid="feedback-table">
      {/* the period the figures cover; what the table shows, from the help center */}
      <div className="flex justify-end items-center gap-2">
        <HelpHint helpKey={HELP.table} align="end" />
        <div className="w-full md:w-[290px]" data-testid="feedback-period">
          <DateFilterDropdown 
            variant="admin" 
            showLabel={false} 
            defaultPreset="lifetime" 
            customPresets={datePresets} 
            value={filter} 
            onChange={setFilter} 
            showArrows t={ts} 
          />
        </div>
      </div>

      {/* the search, in the middle (only products with some feedback are listed) */}
      <div className="flex justify-center">
        <div className="w-full max-w-[640px]" data-testid="feedback-search">
          <Input
            variant="admin"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={showVendor ? t`Product, vendor or comment…` : t`Product or comment…`}
            className="w-full"
            icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />}
          />
        </div>
      </div>

      {/* the whole of it at a glance, compact */}
      {totals && (
        <div className="flex flex-wrap justify-center gap-2" data-testid="feedback-totals">
          {[
            [t`Products with feedback`, totals.products],
            [t`Ratings`, totals.ratings],
            [t`Written reviews`, totals.comments],
            [t`Return requests`, totals.returnRequests],
            [t`Returns`, totals.returns],
          ].map(([label, value]) => (
            <div key={label} className="flex items-baseline gap-2 rounded-lg border border-admin_bordered bg-admin_paper px-3 py-1.5">
              <span className="text-base font-bold tabular-nums">{value}</span>
              <span className="text-xs text-admin_text_secondary">{label}</span>
            </div>
          ))}
        </div>
      )}

      {error && <div className="text-sm text-admin_error">{error}</div>}
      <div className="md:rounded-lg md:border md:border-admin_bordered bg-admin_paper" data-testid="feedback-list">
        <DataTable
          variant="admin_browser"
          preferredHeight="auto"
          apiMethod={apiMethod}
          apiDataField="list"
          showSearch={false}
          renders={columns}
          refreshKey={`${orgId || "all"}|${term}|${rangeKey}|${language}`}
          itemsPerPage={PER_PAGE}
          localUserPreferencesKey="admin-feedback-table"
          showFirstLastPage
          onRowClick={(row) => {
            setProduct(null)
            setOpen(row)
          }}
          mobileCards
          styleOverrides={{ container: "md:h-auto" }}
          noData={
            <span className="text-sm italic text-admin_text_secondary">
              {t`No feedback yet.`}
            </span>
          }
        />
      </div>

      {open && (
        <Modal
          isOpen
          onClose={() => setOpen(null)}
          title={
            <span className="flex items-center gap-3">
              <Thumb src={product?.thumbnail} name={open.name} className="w-12 h-12 rounded-md text-sm" />
              <span className="min-w-0 inline-flex items-center gap-2">
                <span className="min-w-0">
                {product?.url ? (
                  <a 
                    href={product.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block truncate hover:text-admin_primary hover:underline" 
                    title={t`Open in the store`} 
                    data-testid="feedback-product-link"
                  >
                    {open.name}
                  </a>
                ) : (
                  <span className="block truncate">{open.name}</span>
                )}
                {showVendor && open.vendor?.name && <span className="block text-sm font-normal text-admin_text_secondary truncate">
                  {open.vendor.name}
                </span>}
                </span>
                {/* the three tabs, from the help center */}
                <HelpHint helpKey={HELP.product} />
              </span>
            </span>
          }
          className="max-w-[820px]"
        >
          <div className="p-1 min-h-[360px]">
            <ProductFeedback pid={open.pid} locale={locale} language={language} onProduct={setProduct} />
          </div>
        </Modal>
      )}
    </div>
  )
}
